import React, { useMemo, useRef, useEffect } from 'react';
import styles from './Table.module.css';
import SearchBar from '../SearchBar';
import Button from '../Button';
import useTableState from '../../../hooks/useTableState';

/**
 * Table Component
 *
 * A comprehensive table component with CSS Grid, sorting, searching, pagination
 * following the AB Civil design system.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of row objects (used for auto-resize calculations)
 * @param {Array} props.dataToDisplay - Optional array of row objects to display (if different from data)
 * @param {Array} props.columns - Column definitions with the following properties:
 *   - id: string - Unique column identifier
 *   - label: string - Column header text
 *   - field: string - Data field key
 *   - width: number - Initial column width in pixels
 *   - minWidth: number - Minimum column width
 *   - maxWidth: number - Maximum column width
 *   - sortable: boolean - Enable sorting for this column
 *   - searchable: boolean - Include in search filtering
 *   - resizable: boolean - Allow manual resizing
 *   - contentType: string - Content type for auto-resize padding calculation:
 *       'text' (default) - Plain text, minimal padding
 *       'link' - Hyperlinks (phone/email/URLs), same as text
 *       'badge' - Badge components, extra padding for internal spacing
 *       'icon' - Icons/checkmarks, centered with buffer
 *       'date' - Date values, minimal padding
 *       'number' - Numeric values, minimal padding
 *   - formatValue: function - Optional formatter for auto-resize measurement (value, row) => string
 *       Returns the text string that will be displayed (e.g., formatted phone, date, currency)
 *       If not provided, uses String(value). Used for accurate column width calculation.
 *   - render: function - Custom cell renderer (value, row) => ReactNode
 *   - align: string - Text alignment: 'left' | 'center' | 'right'
 * @param {boolean} props.sortable - Enable sorting
 * @param {boolean} props.searchable - Enable search
 * @param {boolean} props.resizable - Enable column resizing
 * @param {boolean} props.paginated - Enable pagination
 * @param {number} props.pageSize - Rows per page
 * @param {function} props.onRowClick - Row click handler
 * @param {function} props.onCellEdit - Cell edit handler
 * @param {function} props.onManualResize - Callback when user manually resizes a column (columnId) => void
 * @param {string} props.groupBy - Group rows by field
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.emptyMessage - Message when no data
 * @param {string} props.variant - Table variant: 'default' | 'compact' | 'dense' | 'comfortable'
 *   - 'dense' (recommended): Compact height (~36px) with readable font (14px)
 *   - 'compact': Very dense (~32px) with smaller font (12px)
 *   - 'default': Balanced (~52px) with readable font (14px)
 *   - 'comfortable': Large rows (~72px) with large font (16px)
 * @param {boolean} props.stickyHeader - Sticky header on scroll
 * @param {boolean} props.autoResizeOnDataChange - Auto-resize columns when data changes
 * @param {boolean} props.showAutoSizeButton - Show auto-size button in toolbar
 * @param {boolean} props.disableResizeListener - Disable window resize auto-sizing listener
 * @param {boolean} props.allowNaturalWidth - Skip viewport compression, use natural content widths with horizontal scrolling
 * @param {string} props.storageKey - Unique key for localStorage persistence
 * @param {string} props.className - Additional CSS classes
 * @param {function} props.getRowClassName - Function to get custom class name for row (row) => string
 */
const Table = ({
  data = [],
  dataToDisplay = null, // Optional: different data to display vs measure for auto-resize
  columns = [],
  sortable = true,
  searchable = true,
  resizable = true,
  paginated = true,
  pageSize = 50,
  onRowClick,
  onCellEdit,
  onManualResize, // Callback when user manually resizes a column
  getRowClassName, // Function to get custom class name for row
  groupBy = null,
  loading = false,
  emptyMessage = 'No data available',
  variant = 'default',
  stickyHeader = true,
  autoResizeOnDataChange = false,
  showAutoSizeButton = true,
  disableResizeListener = false, // Disable window resize auto-sizing listener
  allowNaturalWidth = false, // Skip viewport compression, allow horizontal scrolling
  className = '',
  ...rest
}) => {
  const tableRef = useRef(null);

  // Use table state hook
  // Pass both data (for auto-resize) and dataToDisplay (for rendering)
  const {
    data: displayData,
    totalRows,
    filteredRows,
    allRowsCount,
    currentPage,
    pageSize: currentPageSize,
    totalPages,
    handlePageChange,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    columnWidths,
    handleColumnResize,
    autoSizeColumns,
  } = useTableState(dataToDisplay || data, columns, {
    pageSize,
    sortable,
    searchable,
    paginated,
    storageKey: rest.storageKey || 'table-state', // Allow custom storage key
    variant, // Pass variant for padding calculations
    dataForAutoResize: data, // Pass full data for auto-resize measurements
    containerRef: tableRef, // Pass table container ref for viewport-aware sizing
  });

  // Auto-resize columns when:
  // 1. Toggle is turned ON
  // 2. Data changes (page navigation, filters, etc.)
  // 3. Loading completes
  useEffect(() => {
    if (autoResizeOnDataChange && data.length > 0 && !loading) {
      // Small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        autoSizeColumns(allowNaturalWidth);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, autoResizeOnDataChange, loading]);

  // Re-run auto-resize on window resize (debounced)
  useEffect(() => {
    if (!autoResizeOnDataChange || disableResizeListener) return;

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        autoSizeColumns(allowNaturalWidth);
      }, 150); // 150ms debounce
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [autoResizeOnDataChange, autoSizeColumns, disableResizeListener]);

  // Generate CSS Grid template columns
  const gridTemplateColumns = useMemo(() => {
    return columns
      .map((column) => {
        const width = columnWidths[column.id] || column.width || 150;
        return `${width}px`;
      })
      .join(' ');
  }, [columns, columnWidths]);

  // Handle column resize
  const handleResizeStart = (e, columnId) => {
    if (!resizable) return;

    e.preventDefault();

    const startX = e.clientX;
    const column = columns.find((col) => col.id === columnId);
    const startWidth = columnWidths[columnId] || column?.width || 150;

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(
        column?.minWidth || 100,
        Math.min(startWidth + diff, column?.maxWidth || 500)
      );
      handleColumnResize(columnId, newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // Notify parent that user manually resized a column
      if (onManualResize) {
        onManualResize(columnId);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (!sortable || sortConfig.field !== field) return null;

    return (
      <span className={styles.sortIndicator}>
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const tableClasses = [
    styles.tableContainer,
    styles[variant],
    stickyHeader && styles.stickyHeader,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={tableClasses} ref={tableRef} {...rest}>
      {/* Toolbar */}
      {(searchable || (resizable && showAutoSizeButton)) && (
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            {searchable && (
              <SearchBar
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search table..."
                size="sm"
              />
            )}
          </div>
          <div className={styles.toolbarRight}>
            {resizable && showAutoSizeButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => autoSizeColumns(allowNaturalWidth)}
              >
                Auto-size Columns
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading...</p>
          </div>
        ) : displayData.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{emptyMessage}</p>
          </div>
        ) : (
          <div
            className={styles.table}
            style={{ gridTemplateColumns }}
          >
            {/* Header */}
            {columns.map((column) => (
              <div
                key={column.id}
                className={`${styles.headerCell} ${
                  sortable && column.sortable !== false ? styles.sortable : ''
                }`}
                onClick={() =>
                  sortable && column.sortable !== false && handleSort(column.field)
                }
              >
                <span className={styles.headerContent}>
                  {column.label}
                  {getSortIndicator(column.field)}
                </span>

                {resizable && column.resizable !== false && (
                  <div
                    className={styles.resizeHandle}
                    onMouseDown={(e) => handleResizeStart(e, column.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            ))}

            {/* Rows */}
            {displayData.map((row, rowIndex) => {
              // Get custom row class name if provided
              const rowClassName = getRowClassName ? getRowClassName(row) : '';

              return (
                <React.Fragment key={row.id || rowIndex}>
                  {columns.map((column) => {
                    const value = row[column.field];
                    const cellContent = column.render
                      ? column.render(value, row)
                      : value;

                    return (
                      <div
                        key={`${row.id || rowIndex}-${column.id}`}
                        className={`${styles.cell} ${rowClassName ? styles[rowClassName] : ''}`}
                        onClick={() => onRowClick?.(row)}
                        style={{
                          textAlign: column.align || 'left',
                          justifyContent: column.align === 'center' ? 'center' : column.align === 'right' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        {cellContent}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {paginated && !loading && displayData.length > 0 && (
        <div className={styles.pagination}>
          <div className={styles.paginationInfo}>
            Showing {((currentPage - 1) * currentPageSize) + 1} to{' '}
            {Math.min(currentPage * currentPageSize, totalRows)} of {totalRows} rows
            {filteredRows !== allRowsCount && ` (filtered from ${allRowsCount})`}
          </div>

          <div className={styles.paginationControls}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            <span className={styles.pageNumbers}>
              Page {currentPage} of {totalPages}
            </span>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
